import React, { useContext } from "react";
import PageConfig from "../utils/PageConfig";
import StructuredDataBuilder from "../utils/structuredData/StructuredDataBuilder";
import {
  ContentfulCloudinaryImageComponent,
  MerchandisingProductView,
  Seo,
  Tab,
} from "../types/ContentfulTypes";
import { RatingReviewData } from "@vp/rating-reviews-component";
import Helmet from "react-helmet";
import { CloudinaryVideoMedia } from "../types/CloudinaryTypes";
import { StructuredDataContext } from "../contexts/StructuredDataContextProvider";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";

interface StructuredDataProps {
  mpv: MerchandisingProductView;
  seo?: Seo;
  pageUrl: string;
  canonicalDomain: string;
  defaultVatIncl: boolean;
  reviews: RatingReviewData;
  heroGallery: (ContentfulCloudinaryImageComponent | CloudinaryVideoMedia)[];
  tabs?: Tab[];
}

const StructuredData = (props: StructuredDataProps) => {
  const { mpv, seo, pageUrl, canonicalDomain, defaultVatIncl, reviews, heroGallery, tabs } = props;
  const { structuredDataPrices } = useContext(StructuredDataContext);
  const { locale } = useContext(PageStaticDataContext);

  const clientSidePrices = Object.values(structuredDataPrices ?? {});
  const clientSidePricesExist = clientSidePrices?.length > 0;
  const isSSR = PageConfig.isSSR;
  const alreadyAddedStructuredData = !isSSR && !!window.preload?.appendedStructuredData;

  // Only render structured data prices client-side (as a fallback in case preloading them fails)
  const shouldAddStructuredData = !isSSR && clientSidePricesExist && !alreadyAddedStructuredData;
  const structuredDataBuilder = new StructuredDataBuilder(
    mpv,
    seo,
    pageUrl,
    canonicalDomain,
    defaultVatIncl,
    locale
  );

  const seoStructuredDataProduct = structuredDataBuilder.product(
    clientSidePrices,
    reviews,
    heroGallery
  );

  const faqsTab = tabs?.find(
    (tab: any) => tab.__typename === "tabFaqs" && tab?.faQs && tab?.faQs.length > 0
  );
  const seoStructuredDataFaq = structuredDataBuilder.faq(faqsTab?.faQs || []);

  let seoStructuredData: any[] = [];
  if (locale === "en-GB" || locale === "en-CA") {
    // Temporary: Test this change in UK and CA first
    seoStructuredData = [
      structuredDataBuilder.constructGraph(seoStructuredDataProduct, seoStructuredDataFaq),
    ];
  } else {
    seoStructuredData = [
      structuredDataBuilder.constructGraph(seoStructuredDataProduct),
      seoStructuredDataFaq,
    ];
  }

  const seoVideoData = structuredDataBuilder.video(heroGallery);

  return (
    <>
      {shouldAddStructuredData && (
        <Helmet>
          {seoStructuredData.map((structuredData, i) => (
            <script key={i} type="application/ld+json">
              {JSON.stringify(structuredData, null, 2)}
            </script>
          ))}
          {seoVideoData.map((video, i) => {
            return (
              <script key={i} type="application/ld+json">
                {JSON.stringify(video, null, 2)}
              </script>
            );
          })}
        </Helmet>
      )}
    </>
  );
};

export default StructuredData;
